import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { set } from '@totalsoft/change-tracking-react';
import { Grid, Checkbox, TableRow} from '@mui/material';
import { Autocomplete, TextField, IconButton } from '@totalsoft/rocket-ui';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { Close, Check } from '@mui/icons-material';
import { StyledTableBodyCell } from 'components/common/Styles';
import { emptyString } from 'utils/constants';

const AccountStatementDetailsComponent = ({
  canWrite,
  statementDetailLens,
  statementDetail,
  accountTypes,
  financialIndicatorList,
  onSave,
  onCancelEdit,
  onDelete,
  validation,
  usedByPartner}) => {
  const { t } = useTranslation();

  return (
    <TableRow>
      {statementDetail.isInEditing ? (
        <>
          <StyledTableBodyCell size='small'>
            <TextField
              isNumeric
              fullWidth
              value={(statementDetail.orderNo) || emptyString}
              variant='standard'
              onChange={statementDetailLens.orderNo |> set}
              decimalScale={0}
              disabled={true}
            />
          </StyledTableBodyCell>
          <StyledTableBodyCell size='small'>
            <TextField
              fullWidth
              value={(statementDetail.accountName) || emptyString}
              variant='standard'
              onChange={statementDetailLens.accountName |> set}
              error={!isValid(validation.accountName)}
              helperText={getErrors(validation.accountName)}
            />
          </StyledTableBodyCell>
          <StyledTableBodyCell size='small'>
            <TextField
              fullWidth
              value={(statementDetail.accountSymbol) || emptyString}
              variant='standard'
              onChange={statementDetailLens.accountSymbol |> set}
              error={!isValid(validation.accountSymbol)}
              helperText={getErrors(validation.accountSymbol)}
            />
          </StyledTableBodyCell>
          <StyledTableBodyCell size='small'>
            <Autocomplete
              fullWidth
              valueKey='id'
              labelKey='name'
              value={accountTypes?.find(x => x.id === statementDetail.accountTypeId)}
              options={accountTypes}
              onChange={event => (statementDetailLens.accountTypeId |> set)(event.id)}
              error={!isValid(validation.accountTypeId)}
              helperText={getErrors(validation.accountTypeId)}
            />
          </StyledTableBodyCell>
          <StyledTableBodyCell size='small'>
            <Autocomplete
              fullWidth
              valueKey='id'
              labelKey='name'
              value={financialIndicatorList?.find(x => x.id === statementDetail.financialIndicatorId)}
              options={financialIndicatorList}
              onChange={event => (statementDetailLens.financialIndicatorId |> set)(event.id)}
            />
          </StyledTableBodyCell>
          <StyledTableBodyCell size='small'>
            <TextField
              fullWidth
              value={(statementDetail.attributName) || emptyString}
              variant='standard'
              onChange={statementDetailLens.attributName |> set}
              error={!isValid(validation.attributName)}
              helperText={getErrors(validation.attributName)}
            />
          </StyledTableBodyCell>
          <StyledTableBodyCell size='small'>
            <Checkbox
              disabled={!canWrite}
              checked={(statementDetail.visibleYN) || false}
              onChange={() => set(statementDetailLens.visibleYN)(!statementDetail.visibleYN)}
            />
          </StyledTableBodyCell>
          <StyledTableBodyCell size='small'>
            <Checkbox
              disabled={!canWrite}
              checked={(statementDetail.readOnlyYN) || false}
              onChange={() => set(statementDetailLens.readOnlyYN)(!statementDetail.readOnlyYN)}
            />
          </StyledTableBodyCell>
          <StyledTableBodyCell  size='small'>
            <Grid container wrap='nowrap' justifyContent='flex-end'>
              <IconButton
                type='save'
                fontSize='medium'
                color='transparent'
                tooltip={t('Save')}
                disabled={(statementDetail.isSaving) || !canWrite}
                onClick={onSave}
              />
              <IconButton
                type='cancel'
                fontSize='medium'
                color='transparent'
                tooltip={t('Close')}
                onClick={onCancelEdit}
                disabled={statementDetail.isSaving}
              />
              {usedByPartner ? (
                <></>
              ) : (
                <IconButton
                  type='delete'
                  fontSize='medium'
                  color='transparent'
                  tooltip={t('Delete')}
                  onClick={onDelete}
                  disabled={statementDetail.isSaving || !canWrite}
                />
              )}
            </Grid>
          </StyledTableBodyCell>
        </>
      ) : (
        <>
          <StyledTableBodyCell align='left' size='small' padding='none'>{statementDetail.orderNo}</StyledTableBodyCell>
          <StyledTableBodyCell size='small' padding='none'>{statementDetail.accountName}</StyledTableBodyCell>
          <StyledTableBodyCell size='small' padding='none'>{statementDetail.accountSymbol}</StyledTableBodyCell>
          <StyledTableBodyCell size='small' padding='none'>{accountTypes?.find(x => x.id === statementDetail.accountTypeId)?.name}</StyledTableBodyCell>
          <StyledTableBodyCell size='small' padding='none'>{financialIndicatorList?.find(x => x.id === statementDetail.financialIndicatorId)?.name}</StyledTableBodyCell>
          <StyledTableBodyCell size='small' padding='none'>{statementDetail.attributName}</StyledTableBodyCell>
          <StyledTableBodyCell size='small' padding='none'>
            <>{(statementDetail.visibleYN) ? <Check color='success' /> : <Close />}</>
          </StyledTableBodyCell>
          <StyledTableBodyCell size='small' padding='none'>
            <>{(statementDetail.readOnlyYN) ? <Check color='success' /> : <Close />}</>
          </StyledTableBodyCell>
          <StyledTableBodyCell  size='small' padding='none'>
            <Grid container wrap='nowrap' justifyContent='flex-end'>
              <IconButton
                type='edit'
                fontSize='medium'
                color='transparent'
                tooltip={t('Edit')}
                onClick={() => set(statementDetailLens.isInEditing)(true)}
                disabled={!canWrite}
              />
              {usedByPartner ? (
                <></>
              ) : (
                <IconButton
                  type='delete'
                  fontSize='medium'
                  color='transparent'
                  tooltip={t('Delete')}
                  onClick={onDelete}
                  disabled={!canWrite}
                />
              )}
            </Grid>
          </StyledTableBodyCell>
        </>
      )}
    </TableRow>);
};
AccountStatementDetailsComponent.propTypes = {
  canWrite: PropTypes.bool.isRequired,
  statementDetailLens: PropTypes.object.isRequired,
  statementDetail: PropTypes.object,
  accountTypes: PropTypes.array.isRequired,
  financialIndicatorList: PropTypes.array.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancelEdit: PropTypes.func.isRequired,
  validation: PropTypes.object.isRequired,
  usedByPartner: PropTypes.bool
};

export default AccountStatementDetailsComponent;
